import * as types from "./types";
import type { AxiosInstance, RawAxiosRequestHeaders } from "axios";
import {apiClientEPService, apiClientService} from "../APIClientService";
import { CF } from "../../constants";

class API {
	constructor(private api: AxiosInstance) {}

	public getPreSimulation = async (balance: number, birthdayMonth: number): Promise<types.PreSimulation> => {
		const {
			data: { data },
		} = await this.api.get<types.APIResponse<types.PreSimulation>>("products/loans/v1/fgts/pre-simulation", {
			params: {
				balance,
				birthdayMonth,
			},
			headers: this.baseHeaders(),
		});

		return data;
	};

	/**
	 * Login EP
	 * begin
	 */
	public getUserEligibility = async (cpf: string, productId?: string): Promise<types.UserEligibility> => {
		const { data: { data } } = await this.api.get<types.APIResponse<types.UserEligibility>>(
			`/customers/v1/fgts/vivo/eligible/${cpf}`, {
				data: {cpf, productId},
				headers: {...this.baseHeaders(), "cf-token": CF},
			});

		return data;
	};

	public postAuth = async (authParams: types.IAuthParams) => {
		const {...params } = authParams;
		const response = await apiClientEPService.post<types.IUserStatus>("/auth", { ...params });
		return response;
	};

	public postAuthValidate = async (authValidateParams: types.IAuthValidationParams) => {
		const { publicIp, ...params } = authValidateParams;
		return await apiClientEPService.post("/auth/validate", { ...params }, {headers: { publicIp }});
	};

	/**
	* Login EP
	* end
	*/

	public postAuthorizationToken = async (code: string): Promise<types.AccessToken> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.AccessToken>>(
			"/token",
			{
				code,
			},
			{
				headers: this.baseHeaders(),
			},
		);

		return data;
	};

	public postBalanceInquiryRequest = async (authorizationToken: string): Promise<types.BalanceInquiryRequest> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.BalanceInquiryRequest>>(
			"products/loans/v1/fgts/balance-inquiry-request",
			{},
			{
				headers: this.headersWithAuthentication(authorizationToken),
			},
		);

		return data;
	};

	public getPartialSimulation = async (
		amount: string,
		authorizationToken: string,
	): Promise<types.PartialAnticipationResponse> => {
		const {
			data: { data },
		} = await this.api.get<types.APIResponse<types.PartialAnticipationResponse>>(
			"products/loans/v1/fgts/partial-simulation",
			{
				params: {
					amount,
				},
				headers: this.headersWithAuthentication(authorizationToken),
			},
		);

		return data;
	};

	public getTotalSimulation = async (
		authorizationToken: string,
		yearsInAdvance: number,
	): Promise<types.TotalSimulationResponse> => {
		const {
			data: { data },
		} = await this.api.get<types.APIResponse<types.TotalSimulationResponse>>(
			"products/loans/v1/fgts/total-simulation",
			{
				params: { yearsInAdvance },
				headers: this.headersWithAuthentication(authorizationToken),
			},
		);

		return data;
	};

	public getBalanceInquiryRequest = async (authorizationToken: string): Promise<types.BalanceInquiryRequest> => {
		const {
			data: { data },
		} = await this.api.get<types.APIResponse<types.BalanceInquiryRequest>>(
			"/products/loans/v1/fgts/balance-inquiry-request",
			{ headers: this.headersWithAuthentication(authorizationToken) },
		);

		return data;
	};

	public getCustomerPersonal = async (authorizationToken: string): Promise<types.PersonalCustomer | null> => {
		const {
			data: { data },
		} = await this.api.get<types.APIResponse<types.PersonalCustomer | null>>(
			"/customers/v1/personal/full-registration",
			{
				headers: this.headersWithAuthentication(authorizationToken),
			},
		);

		return data;
	};

	public postCustomerPersonal = async (
		personal: types.PersonalCustomer,
		authorizationToken: string,
	): Promise<types.PersonalCustomer> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PersonalCustomer>>(
			"/customer/v1/fgts/personal/full-registration",
			personal,
			{
				headers: this.headersWithAuthentication(authorizationToken),
			},
		);

		return data;
	};

	public putCustomerPersonal = async (data: types.PersonalCustomer, authorizationToken: string): Promise<void> => {
		await this.api.put("/customer/v1/fgts/personal/full-registration", data, {
			headers: this.headersWithAuthentication(authorizationToken),
		});
	};

	public sendValidationOTPToken = async (
		authToken: string,
		body: types.SendOTPValidationReq,
	): Promise<types.SendOTPValidationRes> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.SendOTPValidationRes>>("/auth/v1/personal/otp", body, {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public getAddressByPostalCode = async (
		cep: string,
		authorizationToken: string,
	): Promise<types.SimpleAddressResponse> => {
		const {
			data: { data },
		} = await this.api.get<types.APIResponse<types.SimpleAddressResponse>>(
			`/customers/v1/personal/address?cep=${cep}`,
			{
				headers: this.headersWithAuthentication(authorizationToken),
			},
		);

		return data;
	};

	public postValidateOTP = async (
		authToken: string,
		body: types.ValidateCodeOTPReq,
	): Promise<types.ValidateCodeOTPRes> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.ValidateCodeOTPRes>>("/auth/v1/personal/otp/validate", body, {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public pathNewTransactionPassword = async (
		authToken: string,
		validationTokenOTP: string,
		body: types.NewPasswordReq,
	): Promise<void> => {
		const {
			data: { data },
		} = await this.api.patch<types.APIResponse<void>>("/auth/v1/personal/password", body, {
			headers: this.headersWithAuthentication(authToken, { validationToken: validationTokenOTP }),
		});

		return data;
	};

	public postTransactionPassword = async (authToken: string, body: types.CreatePasswordReq): Promise<void> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<void>>("/auth/v1/personal/password", body, {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public postCustomersPersonal = async (
		personalInfo: types.PersonalInfo,
		authToken: string,
	): Promise<types.PersonalInfo> => {
		const personalInfoPayload = {
			...personalInfo,
			socialName: personalInfo.fullName,
		};

		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PersonalInfo>>("/customers/v1/personal", personalInfoPayload, {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public patchCustomersPersonal = async (
		personalInfo: types.PersonalInfo,
		authToken: string,
	): Promise<types.PersonalInfo> => {
		const personalInfoPayload = {
			...personalInfo,
			socialName: personalInfo.fullName,
		};

		const {
			data: { data },
		} = await this.api.put<types.APIResponse<types.PersonalInfo>>("/customers/v1/personal", personalInfoPayload, {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public postCustomersPersonalContactsPhones = async (
		contactPhoneInfo: types.PersonalContactPhoneInfo,
		authToken: string,
	): Promise<types.PersonalContactPhoneInfo> => {
		const timestamp = new Date().getTime();
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PersonalContactPhoneInfo>>(
			"/customers/v1/personal/contacts/phones",
			contactPhoneInfo,
			{
				headers: this.headersWithAuthentication(authToken, { "referer": timestamp, "validation-id": timestamp }),
			},
		);

		return data;
	};

	public patchCustomersPersonalContactsPhones = async (
		id: string,
		contactPhoneInfo: types.PersonalContactPhoneInfo,
		authToken: string,
	): Promise<types.PersonalContactPhoneInfo> => {
		const timestamp = new Date().getTime();
		const {
			data: { data },
		} = await this.api.patch<types.APIResponse<types.PersonalContactPhoneInfo>>(
			`/customers/v1/personal/contacts/phones/${id}`,
			contactPhoneInfo,
			{
				headers: this.headersWithAuthentication(authToken, { "referer": timestamp, "validation-id": timestamp }),
			},
		);

		return data;
	};

	public postCustomersPersonalContactsEmails = async (
		contactEmailInfo: types.PersonalContactEmailInfo,
		validationID: string,
		authToken: string,
	): Promise<types.PersonalContactEmailInfo> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PersonalContactEmailInfo>>(
			"/customers/v1/personal/contacts/emails",
			contactEmailInfo,
			{
				headers: this.headersWithAuthentication(authToken, { "validation-id": validationID }),
			},
		);

		return data;
	};

	public patchCustomersPersonalContactsEmails = async (
		contactEmailInfo: types.PersonalContactEmailInfo,
		validationID: string,
		emailId: string,
		authToken: string,
	): Promise<types.PersonalContactEmailInfo> => {
		const {
			data: { data },
		} = await this.api.patch<types.APIResponse<types.PersonalContactEmailInfo>>(
			`/customers/v1/personal/contacts/emails/${emailId}`,
			contactEmailInfo,
			{
				headers: this.headersWithAuthentication(authToken, { "validation-id": validationID }),
			},
		);

		return data;
	};

	public postCustomersPersonalPostalAddress = async (
		postAddressInfo: types.PersonalPostAddressInfo,
		authToken: string,
	): Promise<types.PersonalPostAddressInfo> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PersonalPostAddressInfo>>(
			"/customers/v1/personal/postal-addresses",
			[postAddressInfo],
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);

		return data;
	};

	public patchCustomersPersonalPostalAddress = async (
		id: string,
		postAddressInfo: types.PersonalPostAddressInfo,
		authToken: string,
	): Promise<types.PersonalPostAddressInfo> => {
		const {
			data: { data },
		} = await this.api.patch<types.APIResponse<types.PersonalPostAddressInfo>>(
			`/customers/v1/personal/postal-addresses/${id}`,
			postAddressInfo,
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);

		return data;
	};

	public postCustomersPersonalAffiliation = async (
		affiliationInfo: types.PersonalAffiliationInfo,
		authToken: string,
	): Promise<types.PersonalAffiliationInfo> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PersonalAffiliationInfo>>(
			"/customers/v1/personal/filiation",
			[affiliationInfo],
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);

		return data;
	};

	public patchCustomersPersonalAffiliation = async (
		affiliationInfo: types.PersonalAffiliationInfo,
		authToken: string,
	): Promise<types.PersonalAffiliationInfo> => {
		const {
			data: { data },
		} = await this.api.patch<types.APIResponse<types.PersonalAffiliationInfo>>(
			"/customers/v1/personal/filiation",
			[affiliationInfo],
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);

		return data;
	};

	public postContractSignature = async (
		signatureData: types.SignatureData,
		validationID: string,
		authToken: string,
	): Promise<types.SignatureData> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.SignatureData>>(
			"/products/loans/v1/fgts/contracts/signed",
			signatureData,
			{
				headers: this.headersWithAuthentication(authToken, { "validation-id": validationID }),
			},
		);

		return data;
	};

	// Altera status alreadyRegistration para true
	public postAlreadyRegistration = async (authToken: string): Promise<types.AlreadyRegistration> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.AlreadyRegistration>>(
			"/customers/v1/personal/alreadyRegistration/FGTS/true",
			{},
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);

		return data;
	};

	public postValidatePassword = async (
		passwordData: types.PasswordValidate,
		authToken: string,
	): Promise<types.PasswordValidateResponse> => {
		const {
			data: { data },
		} = await this.api.post<types.APIResponse<types.PasswordValidateResponse>>(
			"/auth/v1/personal/password/validate",
			passwordData,
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);
		return data;
	};

	public getFraudAnalysis = async (authToken: string): Promise<types.APIResponse<types.FraudAnalysis>> => {
		const { data } = await this.api.get<types.APIResponse<types.FraudAnalysis>>("/fraud-analysis/v1?productId=FGTS", {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public postFraudAnalysis = async (
		authorizationToken: string,
		productId: string,
	): Promise<types.APIResponse<types.FraudAnalysis>> => {
		const { data } = await this.api.post<types.APIResponse<types.FraudAnalysis>>("/fraud-analysis/v1", productId, {
			headers: this.headersWithAuthentication(authorizationToken),
		});

		return data;
	};

	public postSaveConsentingTerms = async (
		authToken: string,
		body: types.ConsentingTerms,
	): Promise<types.APIResponse<string>> => {
		const { data } = await this.api.post<types.APIResponse<string>>("/financial-terms/v1/consents", body, {
			headers: this.headersWithAuthentication(authToken),
		});

		return data;
	};

	public verifyEligible = async (authToken: string): Promise<types.APIResponse<types.VerifyEligibleResponse>> => {
		const { data } = await this.api.post<types.APIResponse<types.VerifyEligibleResponse>>(
			"/customers/v1/fgts/contracts/eligible",
			{},
			{
				headers: this.headersWithAuthentication(authToken),
			},
		);

		return data;
	};

	private headersWithAuthentication = (token: string, headers?: RawAxiosRequestHeaders): RawAxiosRequestHeaders =>
		this.baseHeaders({ Authorization: `Bearer ${token}`, ...headers });

	private baseHeaders = (headers?: RawAxiosRequestHeaders): RawAxiosRequestHeaders => ({
		"Content-Type": "application/json",
		"Accept": "application/json",
		...headers,
	});
}

export const api = new API(apiClientService);

export { types as apiTypes };
